import { v4 as randomUUID } from "uuid";
import { TOURNAMENT_GAME_RESULT } from "../../constants";
import { hasCommencedByMoveCount } from "../../utils/match-helper";
const createGameFromPairing = (baseGame, pairing, round) => {
    const game = { ...baseGame };
    assignGamePlayers(game, pairing.whiteUsername, pairing.blackUsername, getScoreBeforeGame(pairing.whiteUsername, round), getScoreBeforeGame(pairing.blackUsername, round));
    return game;
};
export const generateGameId = (roundNumber, board) => {
    return `${("000" + roundNumber).slice(-2)}_${("000" + board).slice(-4)}`;
};
export const initGame = (roundNumber, boardNumber, generateMatchGUID = true) => {
    return {
        gameId: generateGameId(roundNumber, boardNumber),
        roundNumber: roundNumber,
        board: boardNumber,
        matchGUID: generateMatchGUID ? randomUUID() : null,
        // Initialize all player-related fields to null for consistent comparison
        whiteUsername: null,
        blackUsername: null,
        whiteResult: null,
        blackResult: null,
        whiteScoreBeforeGame: null,
        blackScoreBeforeGame: null,
        startTime: null,
        finishTime: null,
    };
};
export const initGames = (roundNumber, count, generateMatchGUID = true) => {
    return Array.from({ length: count }, (_, index) => {
        const boardNumber = index + 1;
        return initGame(roundNumber, boardNumber, generateMatchGUID);
    });
};
export const assignGamePlayers = (game, whiteUsername, blackUsername, whiteScoreBeforeGame, blackScoreBeforeGame) => {
    if (whiteUsername) {
        game.whiteUsername = whiteUsername;
        game.whiteResult = blackUsername
            ? TOURNAMENT_GAME_RESULT.UNFINISHED
            : TOURNAMENT_GAME_RESULT.PAIRINGBYE;
        game.whiteScoreBeforeGame = whiteScoreBeforeGame ?? 0;
    }
    else {
        game.whiteUsername = null;
        game.whiteResult = null;
        game.whiteScoreBeforeGame = null;
    }
    if (blackUsername) {
        game.blackUsername = blackUsername;
        game.blackResult = whiteUsername
            ? TOURNAMENT_GAME_RESULT.UNFINISHED
            : TOURNAMENT_GAME_RESULT.PAIRINGBYE;
        game.blackScoreBeforeGame = blackScoreBeforeGame ?? 0;
    }
    else {
        game.blackUsername = null;
        game.blackResult = null;
        game.blackScoreBeforeGame = null;
    }
    game.startTime = null;
    game.finishTime = null;
};
export const createGamesFromPairings = (pairings, round, generateMatchGUID = true) => {
    const games = initGames(round.roundNumber, pairings.length, generateMatchGUID);
    return games.map((game, index) => createGameFromPairing(game, pairings[index], round));
};
export const getScoreBeforeGame = (username, round) => {
    if (!username)
        return 0;
    if (round.roundNumber === 1)
        return 0;
    if (!round.standings?.length)
        return 0;
    return round.standings.find((s) => s.player.id === username)?.wins ?? 0;
};
export const hasResultMismatch = (game, match, moves) => {
    if (!game.matchRef)
        return false;
    const { whiteResult, blackResult } = getTournamentResultsFromMatch(match, moves);
    if (whiteResult === game.whiteResult && blackResult === game.blackResult) {
        return false;
    }
    console.log(`game ${game.gameId} hasResultMismatch ${whiteResult}-${blackResult} vs ${game.whiteResult}-${game.blackResult}`);
    return true;
};
/**
 * Check if a game has unsaved changes by comparing only user-editable fields
 * @param currentGame - The current game state (with local edits)
 * @param savedGame - The saved game state (from database)
 * @returns true if there are unsaved changes in user-editable fields
 */
export const hasUnsavedChanges = (currentGame, savedGame) => {
    if (!savedGame)
        return false;
    // Only compare user-editable fields, ignore system-generated fields like timestamps
    const fieldsToCompare = [
        "whiteUsername",
        "blackUsername",
        "whiteResult",
        "blackResult",
        "whiteScoreBeforeGame",
        "blackScoreBeforeGame",
    ];
    return fieldsToCompare.some((field) => currentGame[field] !== savedGame[field]);
};
export const getTournamentResultsFromMatch = function (match, moves) {
    let whiteResult = TOURNAMENT_GAME_RESULT.UNFINISHED, blackResult = TOURNAMENT_GAME_RESULT.UNFINISHED;
    if (!match || !match.result) {
        if (!hasCommencedByMoveCount(match, moves)) {
            whiteResult = TOURNAMENT_GAME_RESULT.FORFEITL;
            blackResult = TOURNAMENT_GAME_RESULT.FORFEITL;
        }
    }
    else if (match.result === "forfeit") {
        if (match.player_1_score === 1) {
            whiteResult = TOURNAMENT_GAME_RESULT.FORFEITW;
            blackResult = TOURNAMENT_GAME_RESULT.FORFEITL;
        }
        else if (match.player_2_score === 1) {
            whiteResult = TOURNAMENT_GAME_RESULT.FORFEITL;
            blackResult = TOURNAMENT_GAME_RESULT.FORFEITW;
        }
        else if (match.player_1_score === 0.5) {
            whiteResult = TOURNAMENT_GAME_RESULT.FORFEITD;
            blackResult = TOURNAMENT_GAME_RESULT.FORFEITD;
        }
        else if (match.player_1_score === 0 && match.player_2_score === 0) {
            whiteResult = TOURNAMENT_GAME_RESULT.FORFEITL;
            blackResult = TOURNAMENT_GAME_RESULT.FORFEITL;
        }
    }
    else if (match.result === "abort") {
        whiteResult = TOURNAMENT_GAME_RESULT.FORFEITL;
        blackResult = TOURNAMENT_GAME_RESULT.FORFEITL;
    }
    else {
        if (match.player_1_score === 1) {
            whiteResult = TOURNAMENT_GAME_RESULT.WIN;
            blackResult = TOURNAMENT_GAME_RESULT.LOSS;
        }
        else if (match.player_2_score === 1) {
            whiteResult = TOURNAMENT_GAME_RESULT.LOSS;
            blackResult = TOURNAMENT_GAME_RESULT.WIN;
        }
        else if (match.player_1_score === 0.5) {
            whiteResult = TOURNAMENT_GAME_RESULT.DRAW;
            blackResult = TOURNAMENT_GAME_RESULT.DRAW;
        }
        else {
            whiteResult = TOURNAMENT_GAME_RESULT.LOSS;
            blackResult = TOURNAMENT_GAME_RESULT.LOSS;
        }
    }
    return { whiteResult: whiteResult, blackResult: blackResult };
};
